import { API_BASE_URL } from './config';
import { apiFetch } from './apiFetch';
import type { UserRole } from './roles';

export type UserDto = {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  companyId: string;
  divisionId?: string;
  positionId?: string;
  orgLevelId?: string;
  avatarUrl?: string;
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
};

export async function listUsers(
  companyId: string,
  options?: { role?: UserRole; isActive?: boolean; limit?: number; offset?: number },
) {
  const params = new URLSearchParams();
  if (options?.role) params.set('role', options.role);
  if (options?.isActive !== undefined) params.set('isActive', String(options.isActive));
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));

  const res = await apiFetch(
    `${API_BASE_URL}/api/users/company/${companyId}?${params.toString()}`,
    { headers: { 'Content-Type': 'application/json' } },
  );
  return res.json() as Promise<{ success: boolean; data?: { users: UserDto[]; total: number }; error?: string }>;
}

export async function inviteUser(
  input: { companyId: string; email: string; name: string; role: UserRole; divisionId?: string },
) {
  const res = await apiFetch(`${API_BASE_URL}/api/users/invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return res.json() as Promise<{
    success: boolean;
    data?: { user: UserDto; temporaryPassword: string };
    error?: string;
  }>;
}

export async function updateUserRole(
  userId: string,
  companyId: string,
  role: UserRole,
) {
  const res = await apiFetch(`${API_BASE_URL}/api/users/${userId}/role`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, role }),
  });
  return res.json() as Promise<{ success: boolean; data?: { user: UserDto }; error?: string }>;
}

export async function assignDivision(
  userId: string,
  companyId: string,
  divisionId: string | null,
) {
  const res = await apiFetch(`${API_BASE_URL}/api/users/${userId}/division`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, divisionId }),
  });
  return res.json() as Promise<{ success: boolean; data?: { user: UserDto }; error?: string }>;
}

export async function updateUserPosition(
  userId: string,
  companyId: string,
  positionId: string | null,
) {
  const res = await apiFetch(`${API_BASE_URL}/api/users/${userId}/position`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, positionId }),
  });
  return res.json() as Promise<{ success: boolean; data?: { user: UserDto }; error?: string }>;
}

export async function updateUserOrgLevel(
  userId: string,
  companyId: string,
  orgLevelId: string | null,
) {
  const res = await apiFetch(`${API_BASE_URL}/api/users/${userId}/org-level`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, orgLevelId }),
  });
  return res.json() as Promise<{ success: boolean; data?: { user: UserDto }; error?: string }>;
}

export async function deactivateUser(userId: string, companyId: string) {
  const res = await apiFetch(`${API_BASE_URL}/api/users/${userId}/deactivate`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId }),
  });
  return res.json() as Promise<{ success: boolean; error?: string }>;
}

export async function reactivateUser(userId: string, companyId: string) {
  const res = await apiFetch(`${API_BASE_URL}/api/users/${userId}/reactivate`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId }),
  });
  return res.json() as Promise<{ success: boolean; error?: string }>;
}
